import { createReducer, INITIAL_STATE } from '../../../shared/redux';
import { buildCreateOrderApiConfig, buildEditOrderApiConfig, buildSubmitOrderApiConfig } from './order.api';
import { LOAD_STATE } from '../../../shared/axios';
import { BillingOrderSimulationState } from './simulation/simulation.state';
import { RecurrencePaymentMethod } from '../subscription/subscription.types';

export const CREATE_ORDER_REQUEST_CALLED = '@billing/order/CREATE_ORDER_REQUEST_CALLED';
export const CREATE_ORDER_REQUEST_SUCCEEDED = '@billing/order/CREATE_ORDER_REQUEST_SUCCEEDED';
export const CREATE_ORDER_REQUEST_FAILED = '@billing/order/CREATE_ORDER_REQUEST_FAILED';

export const UPDATE_ORDER_REQUEST_CALLED = '@billing/order/UPDATE_ORDER_REQUEST_CALLED';
export const UPDATE_ORDER_REQUEST_SUCCEEDED = '@billing/order/UPDATE_ORDER_REQUEST_SUCCEEDED';
export const UPDATE_ORDER_REQUEST_FAILED = '@billing/order/UPDATE_ORDER_REQUEST_FAILED';

export const SUBMIT_ORDER = '@billing/order/SUBMIT_ORDER';

export const SUBMIT_ORDER_REQUEST_CALLED = '@billing/order/SUBMIT_ORDER_REQUEST_CALLED';
export const SUBMIT_ORDER_REQUEST_SUCCEEDED = '@billing/order/SUBMIT_ORDER_REQUEST_SUCCEEDED';
export const SUBMIT_ORDER_REQUEST_FAILED = '@billing/order/SUBMIT_ORDER_REQUEST_FAILED';

export const SET_SUBMIT_ORDER_FLAGS = '@billing/order/SET_SUBMIT_ORDER_FLAGS';
export interface BillingCurrentOrderState {
    loadState: string;
    data: {
        id: string;
        payment: {
            intentId: string;
            methodId: string;
            recurrencePayment: RecurrencePaymentMethod;
        };
    };
    error: any;
    updateOrderLoadState: string;
    submitOrder: {
        loadState: any;
    },
}

export interface BillingOrderState {
    simulation: BillingOrderSimulationState;
    currentOrder: BillingCurrentOrderState;
}

export const hasPaymentMethod = state => !!state.billing.order.currentOrder.data.payment.methodId;
export const getCurrentOrder = state => state.billing.order.currentOrder;
export const getSubmitOrderFlags = state => state.billing.order.currentOrder.submitOrder.flags;

interface CreateOrderDTO {
    planId: string;
    addonIds?: any;
}

export const createOrder = ({ planId, addonIds }: CreateOrderDTO) => ({
    type: CREATE_ORDER_REQUEST_CALLED,
    actionTypes: {
        success: CREATE_ORDER_REQUEST_SUCCEEDED,
        fail: CREATE_ORDER_REQUEST_FAILED,
    },
    config: buildCreateOrderApiConfig({
        composition: {
            planId,
            addonsIds: addonIds,
        },
    }),
});

export const updateOrder = ({
    id,
    paymentIntentId,
    composition,
}) => ({
    type: UPDATE_ORDER_REQUEST_CALLED,
    actionTypes: {
        success: UPDATE_ORDER_REQUEST_SUCCEEDED,
        fail: UPDATE_ORDER_REQUEST_FAILED,
    },
    config: buildEditOrderApiConfig({
        id, paymentIntentId, composition,
    }),
});

export const submitOrder = ({ paymentMethodId } = { paymentMethodId: null }) => ({
    type: SUBMIT_ORDER,
    payload: {
        paymentMethodId,
    },
});

export const submitOrderRequest = ({
    id,
    paymentIntentId,
    paymentMethodId,
    recurrencePaymentMethod,
}) => ({
    type: SUBMIT_ORDER_REQUEST_CALLED,
    actionTypes: {
        success: SUBMIT_ORDER_REQUEST_SUCCEEDED,
        fail: SUBMIT_ORDER_REQUEST_FAILED,
    },
    config: buildSubmitOrderApiConfig({
        id, paymentIntentId, paymentMethodId, recurrencePaymentMethod,
    }),
});

export const setSubmitOrderFlags = ({ hasBillingDetails }) => ({
    type: SET_SUBMIT_ORDER_FLAGS,
    payload: {
        flags: {
            hasBillingDetails,
        },
    },
});

const initialState = {
    loadState: LOAD_STATE.LOADING,
    data: {
        id: null,
        payment: {
            intentId: null,
            methodId: null,
            recurrencePaymentMethod: RecurrencePaymentMethod.STRIPE,
        },
    },
    error: null,
    updateOrderLoadState: LOAD_STATE.SUCCESS,
    submitOrder: {
        flags: {
            hasBillingDetails: false,
        },
        loadState: null,
    },
};

export const currentOrderReducers = createReducer({
    [INITIAL_STATE]: initialState,
    [CREATE_ORDER_REQUEST_CALLED]: state => ({
        ...state,
        loadState: LOAD_STATE.LOADING,
        error: null,
    }),
    [CREATE_ORDER_REQUEST_SUCCEEDED]: (state, action) => ({
        ...state,
        loadState: LOAD_STATE.SUCCESS,
        data: action.payload.data,
        error: null,
    }),
    [CREATE_ORDER_REQUEST_FAILED]: state => ({
        ...state,
        ...initialState,
        loadState: LOAD_STATE.FAIL,
        error: null,
    }),
    [UPDATE_ORDER_REQUEST_CALLED]: state => ({
        ...state,
        updateOrderLoadState: LOAD_STATE.LOADING,
        error: null,
    }),
    [UPDATE_ORDER_REQUEST_SUCCEEDED]: (state, action) => ({
        ...state,
        data: {
            ...state.data,
            ...action.payload.data,
            payment: {
                ...action.payload.data.payment,
                methodId: state.data.payment.methodId,
            },
        },
        updateOrderLoadState: LOAD_STATE.SUCCESS,
        error: null,
    }),
    [UPDATE_ORDER_REQUEST_FAILED]: (state, action) => ({
        ...state,
        updateOrderLoadState: null,
        error: action.payload.data,
    }),
    [SUBMIT_ORDER_REQUEST_CALLED]: state => ({
        ...state,
        submitOrder: {
            loadState: LOAD_STATE.LOADING,
        },
    }),
    [SUBMIT_ORDER_REQUEST_SUCCEEDED]: state => ({
        ...state,
        submitOrder: {
            loadState: LOAD_STATE.SUCCESS,
        },
    }),
    [SUBMIT_ORDER_REQUEST_FAILED]: state => ({
        ...state,
        submitOrder: {
            loadState: LOAD_STATE.FAIL,
        },
    }),
    [SET_SUBMIT_ORDER_FLAGS]: (state, action) => ({
        ...state,
        submitOrder: {
            ...state.submitOrder,
            flags: {
                ...state.submitOrder.flags,
                ...action.payload.flags,
            },
        },
    }),
});
